import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, ArrowRight, Sparkles, Users, Utensils, Camera } from 'lucide-react';
import { IMAGES, EVENT_SOLUTIONS, WEDDING_SERVICES, WHY_VIVAMORE } from '../mock/data';
import useScrollReveal from '../hooks/useScrollReveal';
import Seo from '../components/Seo';

const SERVICE_ICONS = [Sparkles, Users, Utensils, Camera];

export default function Events() {
  const tabs = Object.keys(EVENT_SOLUTIONS);
  const [active, setActive] = useState(tabs[0]);
  useScrollReveal([active]);

  return (
    <div>
      <Seo
        title="Events & Celebrations Venue in KL"
        path="/events"
        description="Host birthdays, anniversaries, product launches, corporate events and group gatherings at Vivamore — a refined rooftop event venue at Level 6, NU Sentral Mall, Kuala Lumpur."
        keywords="event venue Kuala Lumpur, birthday venue KL, corporate event venue, product launch venue KL, anniversary celebration, rooftop event space NU Sentral, group dining KL"
      />
      <section className="relative h-[60vh] min-h-[420px] flex items-center justify-center overflow-hidden">
        <img src={IMAGES.wedding1} alt="Events" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-ink/65" />
        <div className="relative z-10 text-center px-6">
          <span className="text-cream/80 text-xs tracking-luxe uppercase">Weddings, Corporate Events & Private Celebrations</span>
          <h1 className="font-display text-5xl md:text-6xl text-cream mt-5">Events</h1>
        </div>
      </section>

      {/* Solutions */}
      <section className="py-24 md:py-32 bg-cream">
        <div className="max-w-7xl mx-auto px-6">
          <div className="reveal text-center flex flex-col items-center mb-12">
            <span className="text-xs tracking-luxe uppercase text-gold">Our Services</span>
            <h2 className="font-display text-4xl md:text-5xl text-ink mt-4">Event Solutions</h2>
            <div className="gold-line mt-6" />
          </div>

          <div className="flex flex-wrap justify-center gap-2 mb-14">
            {tabs.map((t) => (
              <button key={t} onClick={() => setActive(t)} className={`text-xs tracking-widest uppercase px-6 py-3 transition-all duration-300 ${active === t ? 'bg-gold text-cream' : 'text-ink/60 hover:text-gold border border-ink/15'}`}>
                {t}
              </button>
            ))}
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {EVENT_SOLUTIONS[active].map((e, i) => (
              <div key={e.title} className="reveal group bg-white shadow-sm hover:shadow-lg transition-shadow" style={{ transitionDelay: `${i * 0.1}s` }}>
                <div className="img-hover-zoom overflow-hidden h-64">
                  <img src={e.img} alt={e.title} className="w-full h-full object-cover" />
                </div>
                <div className="p-8">
                  <h3 className="font-serif text-2xl text-ink">{e.title}</h3>
                  <p className="text-ink/60 font-light text-sm mt-2 mb-5">{e.desc}</p>
                  <ul className="space-y-2 mb-6">
                    {e.features.map((f) => (
                      <li key={f} className="flex gap-2 text-sm text-ink/70 font-light"><Check size={15} className="text-gold shrink-0 mt-1" /> {f}</li>
                    ))}
                  </ul>
                  <Link to="/plan-event" className="btn-gold inline-flex items-center gap-2 text-xs tracking-widest uppercase px-6 py-3 border border-gold text-gold hover:bg-gold hover:text-cream">
                    Inquire Now <ArrowRight size={14} />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Wedding services */}
      <section className="py-24 md:py-32 bg-ink">
        <div className="max-w-7xl mx-auto px-6">
          <div className="reveal text-center mb-16">
            <h3 className="font-display text-3xl md:text-4xl text-cream">Complete Wedding Services</h3>
            <div className="gold-line mx-auto mt-6" />
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {WEDDING_SERVICES.map((s, i) => {
              const Icon = SERVICE_ICONS[i];
              return (
                <div key={s.title} className="reveal border border-cream/15 p-8 text-center hover:border-gold transition-colors" style={{ transitionDelay: `${i * 0.1}s` }}>
                  <Icon size={30} className="text-gold-light mx-auto" />
                  <h4 className="font-serif text-xl text-cream mt-5">{s.title}</h4>
                  <p className="text-cream/60 font-light text-sm mt-3 leading-relaxed">{s.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Vivamore */}
      <section className="py-24 md:py-32 bg-cream">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-16 items-center">
          <div className="reveal img-hover-zoom overflow-hidden order-2 md:order-1">
            <img src="/interior/event-venue.webp" alt="Event venue" className="w-full h-[520px] object-cover" />
          </div>
          <div className="reveal order-1 md:order-2">
            <span className="text-xs tracking-luxe uppercase text-gold">Why Vivamore</span>
            <h2 className="font-display text-4xl md:text-5xl text-ink mt-4 leading-tight">The Perfect Event Venue</h2>
            <div className="gold-line my-8" />
            <p className="text-ink/70 font-light leading-relaxed mb-8">
              Located in the heart of Kuala Lumpur, Vivamore offers the ideal setting for events of any scale. Our experienced team ensures seamless execution of your special occasions, whether it's a dream wedding or a corporate gathering.
            </p>
            <ul className="space-y-4">
              {WHY_VIVAMORE.map((w) => (
                <li key={w} className="flex gap-3 text-ink/75 font-light"><Check size={18} className="text-gold shrink-0 mt-0.5" /> {w}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-ink">
        <div className="max-w-3xl mx-auto px-6 text-center reveal">
          <h2 className="font-display text-4xl md:text-5xl text-cream">Plan Your Event With Us</h2>
          <div className="gold-line mx-auto my-8" />
          <p className="text-cream/80 font-light text-lg">Let us help you create an unforgettable experience. Contact our events team to discuss your vision.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-10">
            <Link to="/plan-event" className="btn-gold text-xs tracking-widest uppercase px-9 py-4 bg-gold text-cream border border-gold hover:bg-transparent">Request Package</Link>
            <Link to="/contact" className="btn-gold text-xs tracking-widest uppercase px-9 py-4 border border-cream text-cream hover:bg-cream hover:text-ink">Schedule Viewing</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
